<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import type { OrderbookSpread } from '@exchange/libs/orderbook/service/src';

import OrderbookAggregation from './OrderbookAggregation.vue';

const props = defineProps({
  currentSpread: { type: Object as PropType<OrderbookSpread>, required: true },
  lastMatchedPrice: { type: Number, required: true },
  marketPrecision: { type: Number, required: true },
  mini: { type: Boolean, required: true },
  spreadInBps: { type: Boolean, required: true },
});

const { t } = useI18n({ useScope: 'global' });

const spreadNumber = computed(() => (props.spreadInBps ? props.currentSpread.basicPoints : props.currentSpread.quoteCurrency));
const spreadPrecision = computed(() => (props.spreadInBps ? 4 : props.marketPrecision));
const spreadTitle = computed(() => t(props.spreadInBps ? 'modules.orderbook.separator.spread.basePoints' : 'modules.orderbook.separator.spread.quoteCurrency'));
</script>

<template>
  <div class="orderbook-separator">
    <div class="orderbook-separator__item">
      <div class="orderbook-separator__label">{{ $t('modules.orderbook.separator.lastMatchedPriceShort') }}</div>
      <div class="orderbook-separator__value">
        <ot-number-display
          :precision="marketPrecision"
          :number="lastMatchedPrice"
        />
      </div>
    </div>
    <div class="orderbook-separator__item orderbook-separator__item--spread">
      <div class="orderbook-separator__label">{{ spreadTitle }}</div>
      <div class="orderbook-separator__value">
        <ot-number-display
          :precision="spreadPrecision"
          :number="spreadNumber"
        />
      </div>
    </div>
    <div class="orderbook-separator__item">
      <div class="orderbook-separator__label">{{ $t('modules.orderbook.separator.compile') }}</div>
      <orderbook-aggregation
        v-bind="$attrs"
        :mini="mini"
      />
    </div>
  </div>
</template>

<style lang="scss">
.orderbook-separator {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 6px;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);
  background-color: rgb(var(--v-theme-elevation-1));
  gap: 4px;

  &__item {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-xs);
  }

  .xs &__item--spread {
    flex-wrap: wrap;
  }

  &__label {
    color: rgb(var(--v-theme-text-tertiary));
  }
}
</style>
